import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';

// Third party libraries
import { DragDropModule } from '@angular/cdk/drag-drop';
import { DropzoneModule } from 'ngx-dropzone-wrapper';
import { OverlayModule } from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { HighchartsChartModule } from 'highcharts-angular';
import { NgSelectModule } from '@ng-select/ng-select';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { AgGridModule } from 'ag-grid-angular';
import { ScrollingModule } from '@angular/cdk/scrolling';

// PrimeNG components
import { ProgressBarModule } from 'primeng/progressbar';
import { PaginatorModule } from 'primeng/paginator';
import { TextareaModule } from 'primeng/textarea';
import { ButtonModule } from 'primeng/button';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TableModule } from 'primeng/table';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { PopoverModule } from 'primeng/popover';
import { ToastModule } from 'primeng/toast';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { TabsModule } from 'primeng/tabs';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { SelectButtonModule } from 'primeng/selectbutton';
import { IftaLabelModule } from 'primeng/iftalabel';
import { FloatLabelModule } from 'primeng/floatlabel';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { DividerModule } from 'primeng/divider';
import { RadioButtonModule } from 'primeng/radiobutton';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { DrawerModule } from 'primeng/drawer';
import { ChipModule } from 'primeng/chip';
import { CardModule } from 'primeng/card';
import { ListboxModule } from 'primeng/listbox';

// Non 'Ag1' components
import { CartonGroupingPivotTableComponent } from '@app/core/components/carton-grouping-pivot-table/carton-grouping-pivot-table.component';
import { CompanySeasonPaginatorComponent } from './components/company-season-paginator/company-season-paginator.component';
import { ConfirmModalComponent } from './components/confirm-modal/confirm-modal.component';
import { DropdownComponent } from './components/dropdown/dropdown.component';
import { InputModalComponent } from './components/input-modal/input-modal.component';
import { ImageSliderComponent } from './components/image-slider/image-slider.component';
import { ModalBaseComponent } from './components/modal-base/modal-base.component';
import { ModalGalleryComponent } from './components/modal-gallery/modal-gallery.component';
import { PillComponent } from './components/pill/pill.component';
import { PivotTableComponent } from './components/pivot-table/pivot-table.component';
import { RawPivotTableComponent } from './components/raw-pivot-table/raw-pivot-table.component';
import { WeekPickerComponent } from './components/week-picker/week-picker.component';
import { WeekPickerPopupComponent } from './components/week-picker/week-picker-popup/week-picker-popup.component';
import { YearPaginatorComponent } from './components/year-paginator/year-paginator.component';

// Directives components
import { Ag1ChipValueAccessorDirective } from './directives/ag1-chip-value-accessor.directive';
import { Ag1ScrollFieldDirective } from './directives/scroll-field.directive';
import { ClickedOutsideDirective } from './directives/clicked-outside.directive';
import { HasPermissionDirective } from './directives/has-permission.directive';
import { IframeResizerDirective } from './directives/iframe-resizer.directive';
import { LineItemEditableFieldDirective } from './directives/line-item-editable-field.directive';
import { MenuPositionDirective } from './directives/menu-position.directive';
import { ModalHostDirective } from './directives/modal-host.directive';
import { Ag1TooltipDirective } from './directives/tooltip.directive';
import { Ag1TableScrollSyncDirective } from './components/ag1-table/directives/ag1-table-scroll-sync.directive';
import { RevealContentOnHoverDirective } from './directives/reveal-on-hover.directive';
import { Ag1ElementResizeDirective } from './directives/element-resize.directive';

// Pipe components
import { SentenceCasePipe } from './pipes/sentence.pipe';
import { EmptyPipe } from './pipes/empty.pipe';
import { CurrencyPipe } from './pipes/currency.pipe';
import { FilterPipe } from './pipes/filter.pipe';
import { FullUrlPipe } from './pipes/full-url.pipe';
import { HighlightPipe } from './pipes/highlight.pipe';
import { HumanizePipe } from '@app/core/pipes/humanize.pipe';
import { MajorUnitPipe } from './pipes/major-unit.pipe';
import { PackingWeekDatePipe } from './pipes/packing-week-date.pipe';
import { PlurizePipe } from './pipes/plurize.pipe';
import { PrettyPrintPipe } from './pipes/pretty-print.pipe';
import { ReplacePipe } from './pipes/replace.pipe';
import { SafeUrlPipe } from './pipes/safe-url.pipe';
import { SortPipe } from './pipes/sort.pipe';
import { TimeAgoPipe } from './pipes/time-ago.pipe';
import { TruncatePipe } from './pipes/truncate.pipe';
import { TypeofPipe } from './pipes/typeof.pipe';

import { PriceBreakdownComponent } from './price-breakdown/price-breakdown.component';
import { NetReturnGranularityPickerComponent } from './net-return-granularity-picker/net-return-granularity-picker.component';
import { NotificationComponent } from './notification/notification.component';
import { ToolbarService } from './services/toolbar/toolbar.service';
import { SpinnerComponent } from './spinner/spinner.component';
// Shared components
import { PageLoadingPlaceholderComponent } from '@app/shared/page-loading-placeholder/page-loading-placeholder.component';
import { PivotTableColumnLayoutComponent } from '@app/shared/pivot-table-column-layout/pivot-table-column-layout.component';
import { ShimmerLoaderComponent } from '@app/shared/shimmer-loader/shimmer-loader.component';

// Ag1 Components
import { Ag1ProgressRingComponent } from './components/ag1-progress-ring/ag1-progress-ring.component';
import { Ag1WeekRangeComponent } from './components/ag1-week-range/ag1-week-range.component';
import { Ag1ExchangeRatePickerComponent } from './components/ag1-exchange-rate-picker/ag1-exchange-rate-picker.component';
import { Ag1AccordionComponent } from './components/ag1-accordion/ag1-accordion.component';
import { Ag1BadgeComponent } from './components/ag1-badge/ag1-badge.component';
import { Ag1BadgeSolidComponent } from './components/ag1-badge-solid/ag1-badge-solid.component';
import { Ag1BadgeSmallComponent } from './components/ag1-badge-small/ag1-badge-small.component';
import { Ag1ButtonComponent } from './components/ag1-button/ag1-button.component';
import { Ag1ButtonSmallComponent } from './components/ag1-button-small/ag1-button-small.component';
import { Ag1LinkComponent } from './components/ag1-link/ag1-link.component';
import { Ag1CommentComponent } from './components/ag1-comment/ag1-comment.component';
import { Ag1DropdownButtonComponent } from './components/ag1-dropdown-button/ag1-dropdown-button.component';
import { Ag1EmailModalComponent } from './components/ag1-email-modal/ag1-email-modal.component';
import { Ag1FilterGroupComponent } from './components/ag1-filter-group/ag1-filter-group.component';
import { Ag1FilterComponent } from './components/ag1-filter-group/ag1-filter/ag1-filter.component';
import { Ag1InfoComponent } from './components/ag1-info/ag1-info.component';
import { Ag1ListSearchComponent } from './components/ag1-list-search/ag1-list-search.component';
import { Ag1LoaderComponent } from './components/ag1-loader/ag1-loader.component';
import {
  Ag1MenuComponent,
  Ag1ModalContentComponent,
} from './components/ag1-menu/ag1-menu.component';
import { Ag1PaginationComponent } from './components/ag1-pagination/ag1-pagination.component';
import { Ag1PillGroupComponent } from './components/ag1-pill-group/ag1-pill-group.component';
import { Ag1PillComponent } from './components/ag1-pill-group/ag1-pill/ag1-pill.component';
import { FieldDrawerComponent } from './components/pivot-table/field-drawer/field-drawer.component';
import { ToolbarComponent } from './components/pivot-table/toolbar/toolbar.component';
import { Ag1SearchComponent } from './components/ag1-search/ag1-search.component';
import { Ag1TabComponent } from './components/ag1-tab/ag1-tab.component';
import { Ag1TableBaseComponent } from './components/ag1-table/components/ag1-table-base/ag1-table-base.component';
import { Ag1TableDownloadComponent } from './components/ag1-table/components/ag1-table-download/ag1-table-download.component';
import { Ag1TableColumnGroupingComponent } from './components/ag1-table/components/ag1-table-column-grouping/ag1-table-column-grouping.component';
import { Ag1TableColumnSelectionComponent } from './components/ag1-table/components/ag1-table-column-selection/ag1-table-column-selection.component';
import { Ag1TagComponent } from './components/ag1-tag/ag1-tag.component';
import { Ag1TitleComponent } from './components/ag1-title/ag1-title.component';
import { Ag1ToastNotificationComponent } from './components/ag1-toast-notification/ag1-toast-notification.component';
import { Ag1UploadModalComponent } from './components/ag1-upload-modal/ag1-upload-modal.component';
import { Ag1WeekPickerComponent } from './components/ag1-week-picker/ag1-week-picker.component';
import { Ag1DateRangeComponent } from './components/ag1-date-range/ag1-date-range.component';
import { Ag1ViewConfiguration } from './components/ag1-view-configuration/ag1-view-configuration.component';
import { Ag1MillerColumnComponent } from './components/ag1-miller-column/ag1-miller-column.component';
import { Ag1ButtonBaseComponent } from './components/ag1-button-base/ag1-button-base.component';
import { Ag1ChipComponent } from './components/ag1-chip/ag1-chip.component';
import { Ag1PanelComponent } from './components/ag1-panel/ag1-panel.component';
import { Ag1UploadComponent } from './components/ag1-upload/ag1-upload.component';
import { Ag1TooltipContainerComponent } from './components/ag1-tooltip-container/ag1-tooltip-container.component';
import { Ag1FinancialInputComponent } from './components/inputs/ag1-financial-input/ag1-financial-input.component';
import { Ag1SteppedProgressBarComponent } from './components/ag1-stepped-progress-bar/ag1-stepped-progress-bar.component';
import { Ag1TableBaseOldComponent } from './components/ag1-table/components/ag1-table-base/ag1-table-base-old.component';
import { Ag1StepperComponent } from './components/ag1-stepper/ag1-stepper.component';
import { Ag1QueueItemComponent } from './components/ag1-queue-item/ag1-queue-item.component';
import { Ag1QueueMenuComponent } from './components/ag1-queue-menu/ag1-queue-menu.component';
import { Ag1QueueIndicatorComponent } from './components/ag1-queue-indicator/ag1-queue-indicator.component';

// BettaBase Components
import { Ag1GridComponent } from './components/ag1-grid/ag1-grid.component';
import { Ag1GridLoadingOverlayComponent } from './components/ag1-grid/ag1-grid-loading-overlay.component';
import { Ag1ChartComponent } from './components/ag1-chart/ag1-chart.component';
import { Ag1KpiComponent } from './components/ag1-kpi/ag1-kpi.component';
import { Ag1BettabaseErrorComponent } from './components/ag1-bettabase-error/ag1-bettabase-error.component';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { Ag1AvatarComponent } from './components/ag1-avatar/ag1-avatar.component';
import { AgxPivotTableComponent } from './components-openagri/agx-pivot-table/agx-pivot-table.component';
import { AgxPaginatorComponent } from './components-openagri/agx-paginator/agx-paginator.component';
import { Ag1BannerComponent } from './components/ag1-banner/ag1-banner.component';
import { DurationPipe } from './pipes/duration.pipe';
import { Ag1TimeSinceComponent } from './components/ag1-time-since/ag1-time-since.component';
import { Ag1TitleBarComponent } from './components/ag1-title-bar/ag1-title-bar.component';

@NgModule({
  imports: [
    ScrollingModule,
    AgGridModule,
    AngularSvgIconModule,
    CommonModule,
    DragDropModule,
    DropzoneModule,
    FormsModule,
    HighchartsChartModule,
    NgSelectModule,
    OverlayModule,
    PortalModule,
    ReactiveFormsModule,
    RouterModule,
    // PrimeNg modules
    PaginatorModule,
    TextareaModule,
    ButtonModule,
    ToggleSwitchModule,
    TableModule,
    BreadcrumbModule,
    DialogModule,
    InputNumberModule,
    CheckboxModule,
    PopoverModule,
    ToastModule,
    MessageModule,
    TooltipModule,
    TabsModule,
    AvatarModule,
    AvatarGroupModule,
    ConfirmDialogModule,
    SelectButtonModule,
    IftaLabelModule,
    FloatLabelModule,
    AutoCompleteModule,
    DividerModule,
    ProgressBarModule,
    RadioButtonModule,
    InputTextModule,
    SelectModule,
    DrawerModule,
    ChipModule,
    CardModule,
    ListboxModule,
    Ag1TitleBarComponent,
    Ag1TimeSinceComponent,
    Ag1ProgressRingComponent,
    Ag1BannerComponent,
    RevealContentOnHoverDirective,
    AgxPivotTableComponent,
    AgxPaginatorComponent,
    Ag1AvatarComponent,
    Ag1LinkComponent,
    Ag1QueueItemComponent,
    Ag1QueueMenuComponent,
    Ag1QueueIndicatorComponent,
    Ag1ExchangeRatePickerComponent,
    Ag1SteppedProgressBarComponent,
    Ag1StepperComponent,
    Ag1TooltipContainerComponent,
    Ag1TableBaseOldComponent,
    Ag1TableScrollSyncDirective,
    Ag1UploadComponent,
    Ag1FinancialInputComponent,
    Ag1AccordionComponent,
    Ag1BadgeComponent,
    Ag1BadgeSolidComponent,
    Ag1BadgeSmallComponent,
    Ag1ButtonBaseComponent,
    Ag1ButtonComponent,
    Ag1ButtonSmallComponent,
    Ag1ChartComponent,
    Ag1ChipComponent,
    Ag1ChipValueAccessorDirective,
    Ag1ScrollFieldDirective,
    Ag1KpiComponent,
    Ag1BettabaseErrorComponent,
    Ag1CommentComponent,
    Ag1WeekRangeComponent,
    Ag1DateRangeComponent,
    Ag1DropdownButtonComponent,
    Ag1EmailModalComponent,
    Ag1FilterComponent,
    Ag1FilterGroupComponent,
    Ag1GridComponent,
    Ag1GridLoadingOverlayComponent,
    Ag1InfoComponent,
    Ag1ListSearchComponent,
    Ag1LoaderComponent,
    Ag1MenuComponent,
    Ag1MillerColumnComponent,
    Ag1ModalContentComponent,
    Ag1PaginationComponent,
    Ag1PanelComponent,
    Ag1PillGroupComponent,
    Ag1PillComponent,
    Ag1SearchComponent,
    Ag1TabComponent,
    Ag1TableBaseComponent,
    Ag1TableColumnGroupingComponent,
    Ag1TableColumnSelectionComponent,
    Ag1TableDownloadComponent,
    Ag1TagComponent,
    Ag1TitleComponent,
    Ag1ToastNotificationComponent,
    Ag1UploadModalComponent,
    Ag1ViewConfiguration,
    Ag1WeekPickerComponent,
    CartonGroupingPivotTableComponent,
    ClickedOutsideDirective,
    CompanySeasonPaginatorComponent,
    ConfirmModalComponent,
    CurrencyPipe,
    DropdownComponent,
    EmptyPipe,
    FieldDrawerComponent,
    FilterPipe,
    FullUrlPipe,
    HasPermissionDirective,
    HighlightPipe,
    HumanizePipe,
    DurationPipe,
    SentenceCasePipe,
    IframeResizerDirective,
    ImageSliderComponent,
    InputModalComponent,
    LineItemEditableFieldDirective,
    MajorUnitPipe,
    MenuPositionDirective,
    ModalBaseComponent,
    ModalGalleryComponent,
    ModalHostDirective,
    NetReturnGranularityPickerComponent,
    NotificationComponent,
    PackingWeekDatePipe,
    PageLoadingPlaceholderComponent,
    PillComponent,
    PivotTableColumnLayoutComponent,
    PivotTableComponent,
    PlurizePipe,
    PrettyPrintPipe,
    PriceBreakdownComponent,
    RawPivotTableComponent,
    ReplacePipe,
    SafeHtmlPipe,
    SafeUrlPipe,
    ShimmerLoaderComponent,
    SortPipe,
    SpinnerComponent,
    TimeAgoPipe,
    ToolbarComponent,
    TruncatePipe,
    TypeofPipe,
    WeekPickerComponent,
    WeekPickerPopupComponent,
    YearPaginatorComponent,
    Ag1TooltipDirective,
    Ag1ElementResizeDirective,
  ],
  exports: [
    Ag1TitleBarComponent,
    Ag1TimeSinceComponent,
    Ag1ProgressRingComponent,
    Ag1BannerComponent,
    RevealContentOnHoverDirective,
    AgxPivotTableComponent,
    AgxPaginatorComponent,
    Ag1AvatarComponent,
    Ag1LinkComponent,
    Ag1QueueItemComponent,
    Ag1QueueMenuComponent,
    Ag1QueueIndicatorComponent,
    Ag1ExchangeRatePickerComponent,
    Ag1SteppedProgressBarComponent,
    Ag1TooltipContainerComponent,
    Ag1TableBaseOldComponent,
    Ag1TableScrollSyncDirective,
    Ag1UploadComponent,
    Ag1FinancialInputComponent,
    Ag1AccordionComponent,
    Ag1BadgeComponent,
    Ag1BadgeSolidComponent,
    Ag1BadgeSmallComponent,
    Ag1ButtonBaseComponent,
    Ag1ButtonComponent,
    Ag1ButtonSmallComponent,
    Ag1ChartComponent,
    Ag1ChipComponent,
    Ag1CommentComponent,
    Ag1WeekRangeComponent,
    Ag1DateRangeComponent,
    Ag1DropdownButtonComponent,
    Ag1EmailModalComponent,
    Ag1FilterComponent,
    Ag1FilterGroupComponent,
    Ag1GridLoadingOverlayComponent,
    Ag1GridComponent,
    Ag1InfoComponent,
    Ag1KpiComponent,
    Ag1BettabaseErrorComponent,
    Ag1ListSearchComponent,
    Ag1LoaderComponent,
    Ag1MenuComponent,
    Ag1MillerColumnComponent,
    Ag1PaginationComponent,
    Ag1PanelComponent,
    Ag1PillGroupComponent,
    Ag1PillComponent,
    Ag1SearchComponent,
    Ag1TabComponent,
    Ag1TableBaseComponent,
    Ag1TableColumnGroupingComponent,
    Ag1TableColumnSelectionComponent,
    Ag1TableDownloadComponent,
    Ag1TagComponent,
    Ag1TitleComponent,
    Ag1ToastNotificationComponent,
    Ag1UploadModalComponent,
    Ag1ViewConfiguration,
    Ag1WeekPickerComponent,
    Ag1ScrollFieldDirective,
    AngularSvgIconModule,
    CartonGroupingPivotTableComponent,
    ClickedOutsideDirective,
    CompanySeasonPaginatorComponent,
    ConfirmModalComponent,
    CurrencyPipe,
    DropdownComponent,
    EmptyPipe,
    FilterPipe,
    FullUrlPipe,
    HasPermissionDirective,
    HighlightPipe,
    HumanizePipe,
    DurationPipe,
    SentenceCasePipe,
    IframeResizerDirective,
    ImageSliderComponent,
    InputModalComponent,
    LineItemEditableFieldDirective,
    MajorUnitPipe,
    ModalHostDirective,
    MenuPositionDirective,
    ModalBaseComponent,
    ModalGalleryComponent,
    NetReturnGranularityPickerComponent,
    NotificationComponent,
    PackingWeekDatePipe,
    PageLoadingPlaceholderComponent,
    PivotTableColumnLayoutComponent,
    PivotTableComponent,
    PrettyPrintPipe,
    PriceBreakdownComponent,
    RawPivotTableComponent,
    ReplacePipe,
    SafeHtmlPipe,
    SafeUrlPipe,
    ShimmerLoaderComponent,
    SortPipe,
    SpinnerComponent,
    TimeAgoPipe,
    TruncatePipe,
    TypeofPipe,
    WeekPickerComponent,
    YearPaginatorComponent,
    Ag1StepperComponent,
    Ag1TooltipDirective,
    Ag1ElementResizeDirective,
    // PrimeNg modules
    PaginatorModule,
    TextareaModule,
    ToggleSwitchModule,
    TableModule,
    BreadcrumbModule,
    DialogModule,
    InputNumberModule,
    CheckboxModule,
    PopoverModule,
    ToastModule,
    MessageModule,
    ButtonModule,
    ProgressBarModule,
    TooltipModule,
    TabsModule,
    AvatarModule,
    AvatarGroupModule,
    ConfirmDialogModule,
    SelectButtonModule,
    IftaLabelModule,
    FloatLabelModule,
    AutoCompleteModule,
    DividerModule,
    RadioButtonModule,
    InputTextModule,
    SelectModule,
    DrawerModule,
    ChipModule,
    CardModule,
    ListboxModule,
  ],
  providers: [
    HumanizePipe,
    DurationPipe,
    PlurizePipe,
    SentenceCasePipe,
    ToolbarService,
  ],
})
export class CoreModule {}
